import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Guitar, Award, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';
import { PublicHeader, PublicFooter } from '@/components/PublicShell';
import { formatDateShortPt } from '@/components/Badges';

export default function RankingEliminatoriasPage() {
  const [entries, setEntries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/public/ranking-eliminatorias')
      .then((r) => setEntries(r.data))
      .finally(() => setLoading(false));
    api.get('/public/settings').then((r) => setSettings(r.data));
  }, []);

  return (
    <div>
      <PublicHeader />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <TrendingUp className="w-6 h-6 text-[var(--g66-amber)]" />
          <div className="font-display text-4xl text-white">MELHOR BANDA DAS ELIMINATÓRIAS</div>
        </div>
        <p className="text-muted-foreground">
          Ranking atualizado automaticamente a cada resultado de etapa eliminatória.
          O 1º colocado ganha vaga direta na <span className="text-[var(--g66-amber)] font-semibold">Semifinal</span>.
        </p>
        <div className="mt-3 text-xs text-muted-foreground bg-[var(--g66-surface)] border border-border rounded-lg p-3">
          <strong className="text-white">Critérios (nesta ordem):</strong>
          <ol className="list-decimal ml-5 mt-1 space-y-0.5">
            <li>Maior percentual de votos válidos na noite</li>
            <li>Maior número absoluto de votos</li>
            <li>Maior vantagem em pontos percentuais sobre a 2ª colocada da noite</li>
            <li>Menor penalidade aplicada</li>
            <li>Em caso de empate final: sorteio pela organização</li>
          </ol>
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Carregando ranking…</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">Nenhuma etapa eliminatória finalizada ainda.</div>
        ) : (
          <Card className="mt-6 border border-border bg-card">
            <CardContent className="p-0">
              <ul className="divide-y divide-border">
                {entries.map((e, i) => {
                  const isTop = i === 0;
                  const tied = e.tied_top;
                  return (
                    <li
                      key={`${e.stage_id}-${e.band?.id}`}
                      className={`p-4 flex items-center gap-4 ${isTop ? 'bg-[color:hsla(42,92%,52%,0.07)]' : ''}`}
                      data-testid={`ranking-item-${i}`}
                    >
                      <div className={`w-10 text-center font-display text-3xl ${isTop ? 'text-[var(--g66-amber-2)]' : 'text-muted-foreground'}`}>
                        {i + 1}
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-[#0b0c0f] flex items-center justify-center border border-border overflow-hidden shrink-0">
                        {e.band?.logo_url
                          ? <img src={e.band.logo_url} alt={e.band.name} className="w-full h-full object-cover" />
                          : <Guitar className="w-5 h-5 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/bandas/${e.band?.slug}`} className="font-semibold text-white hover:text-[var(--g66-amber)]">
                            {e.band?.name}
                          </Link>
                          {isTop && !tied && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--g66-amber)] border border-[color:hsla(42,92%,52%,0.5)] rounded-full px-2 py-0.5">
                              <Award className="w-3 h-3" /> Vaga direta na semifinal
                            </span>
                          )}
                          {tied && (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--g66-red-2)] border border-[color:hsla(0,78%,52%,0.5)] rounded-full px-2 py-0.5">
                              Empate — desempate manual
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Venceu em <Link to={`/etapas/${e.stage_slug}`} className="underline">{e.stage_title}</Link>
                          {' '}· {formatDateShortPt(e.stage_date)}
                          {e.bands_count === 3 && ' · noite de 3 bandas'}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Vantagem sobre 2º lugar: <span className="text-white">{e.vote_advantage_pct?.toFixed?.(1) || 0} p.p.</span>
                          {e.penalty_percentage > 0 && (
                            <> · Penalidade: <span className="text-[var(--g66-red-2)]">-{e.penalty_percentage}%</span></>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display text-3xl text-white">{e.winner_percentage?.toFixed?.(1) || 0}%</div>
                        <div className="text-xs text-muted-foreground">{e.winner_votes} votos</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
      <PublicFooter settings={settings} />
    </div>
  );
}
